import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckSquare, Square, Plus, X as XIcon, RotateCcw, Sparkles,
  Crown, Loader2, ChevronRight, Lock, Tent, Save, History, Trash2, ChevronDown,
} from "lucide-react";
import Header from "../components/app/Header";
import Footer from "../components/app/Footer";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * /checklist — Camping Checklist (Pro perk)
 *
 * • Free / signed-out users see a beautiful read-only sample list with an
 *   "Unlock with Pro" CTA. Backend serves this via /api/checklist/preview
 *   (public, no auth).
 *
 * • Pro users get the real, server-backed checklist: tick items off (auto-
 *   saves), add custom items, delete items, reset all checks after a trip,
 *   restore defaults, and tap "AI top-up" to ask Camper AI for trip-tuned
 *   additions.
 *
 * State strategy: optimistic UI for ticks (snappy feel), then sync to
 * backend. On failure we revert and toast.
 */
const CATEGORY_LABELS = {
  Shelter: "🏕 Shelter",
  Sleep: "🛌 Sleep",
  Kitchen: "🍳 Kitchen",
  Clothing: "🧥 Clothing",
  Personal: "🧴 Personal",
  Documents: "📋 Documents",
  Extras: "✨ Extras",
  Custom: "📝 My own",
};
const CATEGORY_ORDER = ["Shelter", "Sleep", "Kitchen", "Clothing", "Personal", "Documents", "Extras", "Custom"];

export default function ChecklistPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPro = !!user?.is_pro || (user?.role === "admin");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [newText, setNewText] = useState("");
  const [newCat, setNewCat] = useState("Custom");
  const [days, setDays] = useState(2);
  const [party, setParty] = useState("2 adults");
  const [recentlyAdded, setRecentlyAdded] = useState(new Set());
  const [trips, setTrips] = useState([]);
  const [mostForgotten, setMostForgotten] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [expandedTrip, setExpandedTrip] = useState(null);

  // Initial load — Pro hits the real endpoint, everyone else hits preview.
  useEffect(() => {
    let alive = true;
    const url = isPro ? "/checklist" : "/checklist/preview";
    api.get(url)
      .then(({ data }) => { if (alive) setItems(data.items || []); })
      .catch((e) => { if (alive) toast.error(formatErr(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [isPro]);

  // Pro-only: load trip history alongside the checklist
  useEffect(() => {
    if (!isPro) return;
    let alive = true;
    api.get("/checklist/trips")
      .then(({ data }) => {
        if (!alive) return;
        setTrips(data.trips || []);
        setMostForgotten(data.most_forgotten || []);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [isPro]);

  const reloadTrips = useCallback(async () => {
    try {
      const { data } = await api.get("/checklist/trips");
      setTrips(data.trips || []);
      setMostForgotten(data.most_forgotten || []);
    } catch {}
  }, []);

  // Group by category
  const grouped = useMemo(() => {
    const buckets = {};
    for (const it of items) {
      const cat = CATEGORY_ORDER.includes(it.category) ? it.category : "Custom";
      (buckets[cat] = buckets[cat] || []).push(it);
    }
    return CATEGORY_ORDER.map((cat) => [cat, buckets[cat] || []]).filter(([, arr]) => arr.length);
  }, [items]);

  const totalChecked = items.filter((i) => i.checked).length;
  const pct = items.length ? Math.round((totalChecked / items.length) * 100) : 0;

  // --- Pro-only mutators (no-ops if not Pro) ---
  const toggleItem = async (item) => {
    if (!isPro) return;
    const next = !item.checked;
    setItems((cur) => cur.map((it) => it.item_id === item.item_id ? { ...it, checked: next } : it));
    try {
      await api.patch(`/checklist/items/${item.item_id}`, { checked: next });
    } catch (e) {
      setItems((cur) => cur.map((it) => it.item_id === item.item_id ? { ...it, checked: !next } : it));
      toast.error(formatErr(e));
    }
  };

  const addItem = async (e) => {
    e?.preventDefault();
    if (!isPro || !newText.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/checklist/items", { text: newText.trim(), category: newCat });
      const newItem = data.items[data.items.length - 1];
      setItems(data.items);
      setNewText("");
      setRecentlyAdded((s) => new Set(s).add(newItem.item_id));
      setTimeout(() => setRecentlyAdded((s) => { const n = new Set(s); n.delete(newItem.item_id); return n; }), 2000);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const deleteItem = async (item) => {
    if (!isPro) return;
    setItems((cur) => cur.filter((it) => it.item_id !== item.item_id));
    try {
      await api.delete(`/checklist/items/${item.item_id}`);
    } catch (e) {
      // Reload from server on failure to avoid drift
      const { data } = await api.get("/checklist");
      setItems(data.items || []);
      toast.error(formatErr(e));
    }
  };

  const resetAll = async () => {
    if (!isPro) return;
    if (!window.confirm("Un-tick every item (you'll keep your list)? This is good for the start of a new trip.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/checklist/reset");
      setItems(data.items);
      toast.success("Fresh slate — happy packing!");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const restoreDefaults = async () => {
    if (!isPro) return;
    if (!window.confirm("Replace your list with the default 45 items? Your custom items will be lost.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/checklist/restore-defaults");
      setItems(data.items);
      toast.success("Defaults restored");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const saveTrip = async () => {
    if (!isPro) return;
    if (totalChecked === 0) {
      toast.error("Tick at least one item before saving the trip.");
      return;
    }
    const name = window.prompt(
      `Name this trip (optional)\n\nYou packed ${totalChecked} of ${items.length} items. Saving will reset all checks for the next trip.`,
      `Trip · ${new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`
    );
    if (name === null) return; // user cancelled
    setBusy(true);
    try {
      const { data } = await api.post("/checklist/save-trip", { name: name || undefined });
      toast.success(`Saved "${data.name}" · packed ${data.packed_count}/${data.total_count}`);
      // Refresh list (now unchecked) + trips
      const { data: cl } = await api.get("/checklist");
      setItems(cl.items || []);
      await reloadTrips();
      setHistoryOpen(true);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const deleteTrip = async (trip) => {
    if (!window.confirm(`Delete "${trip.name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/checklist/trips/${trip.trip_id}`);
      toast.success("Trip deleted");
      await reloadTrips();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const aiTopUp = async () => {
    if (!isPro) return;
    setAiBusy(true);
    try {
      const { data } = await api.post("/checklist/generate", { days, party });
      if (data.added?.length) {
        const newIds = new Set(data.added.map((i) => i.item_id));
        setItems((cur) => [...cur, ...data.added]);
        setRecentlyAdded((s) => new Set([...s, ...newIds]));
        toast.success(`Camper AI added ${data.added.length} item${data.added.length === 1 ? "" : "s"}`);
        setTimeout(() => setRecentlyAdded(new Set()), 3500);
      } else {
        toast.info("AI didn't suggest anything new — looks like you're all set!");
      }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setAiBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#fafafa]" data-testid="checklist-page">
      <Header />

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-8">
        {/* Title row */}
        <div className="mb-6">
          <div className="eyebrow text-[#ff4500] flex items-center gap-1.5">
            <Crown className="w-3 h-3" /> Pro perk
          </div>
          <h1 className="font-display text-3xl sm:text-4xl mt-1 flex items-center gap-3">
            <Tent className="w-7 h-7 text-[#ff4500]" strokeWidth={1.6} />
            Camping checklist
          </h1>
          <p className="text-sm text-[#555] mt-2 leading-relaxed max-w-2xl">
            {isPro
              ? "Tick items off as you pack. Add your own. Tap 'AI top-up' and Camper AI will suggest extras tailored to your trip. Auto-saved — leave and come back whenever."
              : "Your personal, always-saved camping checklist — tick off as you pack, add your own items, and ask Camper AI for trip-specific extras. Part of CampCompass Pro."}
          </p>
        </div>

        {/* Progress bar */}
        {items.length > 0 && (
          <div className="mb-6" data-testid="checklist-progress">
            <div className="flex items-center justify-between mb-1.5 text-xs">
              <span className="mono uppercase tracking-wider text-[#555]">
                {totalChecked} / {items.length} packed · {pct}%
              </span>
              {isPro && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={saveTrip}
                    disabled={busy || totalChecked === 0}
                    className="text-[11px] mono uppercase tracking-wider text-[#ff4500] hover:text-[#cc3300] disabled:opacity-40 inline-flex items-center gap-1"
                    data-testid="save-trip-btn"
                    title="Save this packing run as a completed trip + reset checks"
                  >
                    <Save className="w-3 h-3" /> Save trip
                  </button>
                  <span className="text-[#ccc]">·</span>
                  <button
                    onClick={resetAll}
                    disabled={busy || totalChecked === 0}
                    className="text-[11px] mono uppercase tracking-wider text-[#888] hover:text-[#ff4500] disabled:opacity-40 inline-flex items-center gap-1"
                    data-testid="reset-checks-btn"
                  >
                    <RotateCcw className="w-3 h-3" /> Reset
                  </button>
                </div>
              )}
            </div>
            <div className="h-1.5 bg-[#eee] overflow-hidden">
              <div
                className="h-full bg-[#ff4500] transition-all duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}

        {/* Pro upsell banner (free users only) */}
        {!isPro && !loading && (
          <ProUpsellBanner navigate={navigate} userExists={!!user} />
        )}

        {/* AI top-up + Add custom (Pro only) */}
        {isPro && (
          <>
            {/* "You usually forget" reminder pill — surfaces the top-3 items the
                user has historically failed to pack so they remember this time. */}
            {mostForgotten.length > 0 && (
              <div className="mb-4 px-3.5 py-2.5 border-l-4 border-[#ff4500] bg-[#fff8f5] text-[12px]" data-testid="forgotten-pill">
                <div className="eyebrow text-[#ff4500] mb-0.5">You usually forget</div>
                <div className="text-[#333]">
                  {mostForgotten.slice(0, 4).map((m, i, arr) => (
                    <span key={m.item}>
                      <strong>{m.item}</strong>
                      <span className="text-[#888] mono text-[10px] ml-0.5">×{m.count}</span>
                      {i < arr.length - 1 && <span className="text-[#888]"> · </span>}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-4 p-4 border hairline bg-[#fffdfa]" data-testid="ai-topup-box">
              <div className="flex items-center gap-2 mb-2.5">
                <Sparkles className="w-4 h-4 text-[#ff4500]" strokeWidth={1.5} />
                <div className="eyebrow">AI top-up · Camper AI</div>
              </div>
              <p className="text-[12px] text-[#555] mb-3 leading-relaxed">
                Tell Camper AI about your trip and it'll suggest 6-10 extra items tailored to the conditions, party and length of stay.
              </p>
              <div className="grid sm:grid-cols-[90px_1fr_auto] gap-2 items-end">
                <div>
                  <div className="eyebrow text-[10px] mb-1">Nights</div>
                  <input type="number" min={1} max={30} value={days}
                    onChange={(e) => setDays(Number(e.target.value) || 1)}
                    className="w-full border hairline p-2 text-sm mono focus:outline-none focus:border-[#ff4500]"
                    data-testid="ai-days" />
                </div>
                <div>
                  <div className="eyebrow text-[10px] mb-1">Party</div>
                  <input value={party}
                    onChange={(e) => setParty(e.target.value)}
                    placeholder="2 adults + 1 dog"
                    className="w-full border hairline p-2 text-sm focus:outline-none focus:border-[#ff4500]"
                    data-testid="ai-party" />
                </div>
                <button onClick={aiTopUp} disabled={aiBusy}
                  className="btn-primary !px-4 !py-2 text-xs inline-flex items-center gap-1.5"
                  data-testid="ai-topup-btn">
                  {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  Top up
                </button>
              </div>
            </div>

            {/* Add own item */}
            <form onSubmit={addItem} className="mb-6 p-4 border hairline bg-white flex items-end gap-2 flex-wrap" data-testid="add-item-form">
              <div className="flex-1 min-w-[180px]">
                <div className="eyebrow text-[10px] mb-1">Add your own</div>
                <input value={newText} onChange={(e) => setNewText(e.target.value)}
                  placeholder="e.g. Marshmallow forks"
                  className="w-full border hairline p-2 text-sm focus:outline-none focus:border-[#ff4500]"
                  data-testid="new-item-text" />
              </div>
              <div>
                <div className="eyebrow text-[10px] mb-1">Category</div>
                <select value={newCat} onChange={(e) => setNewCat(e.target.value)}
                  className="border hairline p-2 text-sm focus:outline-none focus:border-[#ff4500]"
                  data-testid="new-item-cat">
                  {CATEGORY_ORDER.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
                </select>
              </div>
              <button type="submit" disabled={busy || !newText.trim()}
                className="btn-secondary !px-3 !py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
                data-testid="add-item-btn">
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            </form>
          </>
        )}

        {/* Loading / empty */}
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : items.length === 0 ? (
          <div className="p-10 border hairline bg-white text-center text-sm text-[#888]">
            Empty — tap "Restore defaults" to get the starter list back.
          </div>
        ) : (
          <div className="space-y-5">
            {grouped.map(([cat, arr]) => (
              <CategoryBlock
                key={cat}
                category={cat}
                items={arr}
                isPro={isPro}
                recentlyAdded={recentlyAdded}
                onToggle={toggleItem}
                onDelete={deleteItem}
              />
            ))}
          </div>
        )}

        {/* Restore defaults footer (Pro only) */}
        {isPro && items.length > 0 && (
          <div className="mt-8 text-center">
            <button onClick={restoreDefaults} disabled={busy}
              className="text-[11px] mono uppercase tracking-wider text-[#888] hover:text-[#ff4500] disabled:opacity-40"
              data-testid="restore-defaults-btn">
              ↻ Restore default 45-item starter list
            </button>
          </div>
        )}

        {/* Trip history (Pro only) — collapsible, shows packed/missed per trip */}
        {isPro && (
          <section className="mt-10 border-t hairline pt-6" data-testid="trip-history">
            <button
              onClick={() => setHistoryOpen((o) => !o)}
              className="w-full flex items-center justify-between gap-3 text-left mb-3"
              data-testid="trip-history-toggle"
            >
              <div>
                <div className="eyebrow text-[#ff4500] flex items-center gap-1.5">
                  <History className="w-3 h-3" /> Trip history
                </div>
                <h2 className="font-display text-xl mt-1">
                  {trips.length === 0
                    ? "No trips yet — save your first when you get home"
                    : `${trips.length} saved ${trips.length === 1 ? "trip" : "trips"}`}
                </h2>
              </div>
              <ChevronDown className={`w-5 h-5 text-[#888] transition-transform ${historyOpen ? "rotate-180" : ""}`} />
            </button>

            {historyOpen && trips.length > 0 && (
              <div className="space-y-2.5" data-testid="trip-list">
                {trips.map((t) => {
                  const pct = t.total_count ? Math.round((t.packed_count / t.total_count) * 100) : 0;
                  const isOpen = expandedTrip === t.trip_id;
                  return (
                    <article key={t.trip_id} className="border hairline bg-white" data-testid={`trip-${t.trip_id}`}>
                      <header className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <div className="font-display text-base truncate">{t.name}</div>
                          <div className="text-[11px] mono text-[#888] mt-0.5">
                            {new Date(t.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                            {" · "}
                            <span className={pct >= 90 ? "text-[#0a8500]" : pct >= 70 ? "text-[#ff4500]" : "text-[#b00020]"}>
                              {t.packed_count}/{t.total_count} packed · {pct}%
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {t.missed_count > 0 && (
                            <button
                              onClick={() => setExpandedTrip(isOpen ? null : t.trip_id)}
                              className="btn-secondary !px-2.5 !py-1 text-[10px]"
                              data-testid={`trip-toggle-${t.trip_id}`}
                            >
                              {isOpen ? "Hide" : `${t.missed_count} forgotten`}
                            </button>
                          )}
                          <button
                            onClick={() => deleteTrip(t)}
                            className="text-[#bbb] hover:text-[#b00020] p-1"
                            aria-label="Delete trip"
                            data-testid={`trip-del-${t.trip_id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </header>
                      {isOpen && (t.missed || []).length > 0 && (
                        <div className="px-4 pb-3 pt-1 text-[12px] text-[#555] border-t hairline bg-[#fafafa]">
                          <div className="eyebrow text-[10px] mb-1.5 text-[#b00020]">Forgotten on this trip</div>
                          <div className="flex flex-wrap gap-1.5">
                            {t.missed.map((m, i) => (
                              <span key={i} className="bg-white border hairline px-2 py-0.5 mono text-[11px]">{m}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}

            {historyOpen && trips.length === 0 && (
              <p className="text-sm text-[#888] leading-relaxed">
                Saved trips appear here. Tick items off as you pack, hit <strong className="text-[#ff4500]">Save trip</strong> at the top when you're done — we'll record what you packed and forgot. Next time, Camper AI uses that to remind you of items you usually leave behind.
              </p>
            )}
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}

// ---- Subcomponents ---------------------------------------------------------

function CategoryBlock({ category, items, isPro, recentlyAdded, onToggle, onDelete }) {
  const allChecked = items.every((i) => i.checked);
  return (
    <section className="border hairline bg-white" data-testid={`category-${category}`}>
      <header className={`px-4 py-2.5 border-b hairline flex items-center justify-between ${allChecked ? "bg-[#f4fff0]" : "bg-[#fafafa]"}`}>
        <div className="font-display text-base flex items-center gap-2">
          <span>{CATEGORY_LABELS[category] || category}</span>
          {allChecked && <span className="text-[10px] mono uppercase text-[#0a8500]">✓ done</span>}
        </div>
        <div className="text-[11px] mono text-[#888]">
          {items.filter((i) => i.checked).length}/{items.length}
        </div>
      </header>
      <ul>
        {items.map((it) => (
          <li
            key={it.item_id}
            className={`px-4 py-2.5 border-b hairline last:border-b-0 flex items-center gap-3 group transition-colors ${
              recentlyAdded.has(it.item_id) ? "bg-[#fff8f5]" : ""
            }`}
            data-testid={`item-${it.item_id}`}
          >
            <button
              onClick={() => onToggle(it)}
              disabled={!isPro}
              className={`flex-shrink-0 transition-colors ${isPro ? "cursor-pointer" : "cursor-not-allowed"}`}
              aria-label={it.checked ? `Mark ${it.text} as not packed` : `Mark ${it.text} as packed`}
              data-testid={`toggle-${it.item_id}`}
            >
              {it.checked
                ? <CheckSquare className="w-5 h-5 text-[#ff4500]" strokeWidth={2} />
                : <Square className="w-5 h-5 text-[#bbb]" strokeWidth={1.6} />}
            </button>
            <span className={`flex-1 text-sm ${it.checked ? "line-through text-[#999]" : "text-[#222]"}`}>
              {it.text}
            </span>
            {isPro && (
              <button
                onClick={() => onDelete(it)}
                className="opacity-0 group-hover:opacity-100 text-[#bbb] hover:text-[#b00020] transition"
                aria-label={`Remove ${it.text}`}
                data-testid={`delete-${it.item_id}`}
              >
                <XIcon className="w-4 h-4" />
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function ProUpsellBanner({ navigate, userExists }) {
  return (
    <div className="mb-6 border-2 border-[#ff4500] bg-gradient-to-br from-[#ff4500] to-[#cc3300] text-white p-5" data-testid="checklist-upsell">
      <div className="flex items-start gap-3">
        <Lock className="w-5 h-5 flex-shrink-0 mt-1" strokeWidth={2} />
        <div className="flex-1">
          <div className="eyebrow text-white/80 mb-1">Pro perk · Try free for 14 days</div>
          <h2 className="font-display text-xl sm:text-2xl leading-tight mb-2">
            Unlock your personal camping checklist
          </h2>
          <ul className="text-sm space-y-1 mb-4 text-white/95">
            <li>✓ Tick items off as you pack — auto-saved to your account</li>
            <li>✓ Add your own custom items (Marshmallow forks, anyone?)</li>
            <li>✓ AI top-up: extras tuned to your campsite, party & nights</li>
            <li>✓ Reset checks at the start of every new trip</li>
            <li>✓ Works on your phone offline once cached</li>
          </ul>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate("/pro")}
              className="bg-white text-[#ff4500] font-bold uppercase tracking-wider text-xs px-4 py-2.5 hover:bg-[#fff8f5] inline-flex items-center gap-2"
              data-testid="checklist-upgrade-btn"
            >
              <Crown className="w-4 h-4" /> {userExists ? "Try Pro free for 14 days" : "Sign up & try Pro free"}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[11px] text-white/80 mt-3">£1.99/mo after the trial. Cancel anytime. The preview below shows the starter items you'd unlock.</p>
        </div>
      </div>
    </div>
  );
}
